import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { initAnalytics, capturePageview } from './lib/analytics';
import { initSentry, SentryErrorBoundary } from './lib/sentry';
import { AtomsDemo } from './pages/AtomsDemo';
import { OrganismsDemo } from './pages/OrganismsDemo';
import { LoginPage } from './pages/LoginPage';
import { LegalPrivacy } from './pages/LegalPrivacy';
import { LegalDemoNotice } from './pages/LegalDemoNotice';
import { Dashboard } from './pages/Dashboard';
import { BuildMode } from './pages/BuildMode';
import { BuildReview } from './pages/BuildReview';
import { ClientsList } from './pages/ClientsList';
import { ClientDetail } from './pages/ClientDetail';
import { MattersIndex } from './law/pages/MattersIndex';
import { MatterDetail } from './law/pages/MatterDetail';
// Side-effect import: AppealStack Law self-registers its entity-tab contributions (e.g. the
// client "Matters" tab) into the core tab registry. The single law→app FE seam; inert when
// the edition is off.
import './law/tabs';
import { InvoiceTermsDetail } from './pages/InvoiceTermsDetail';
import { ClientSetupWizard } from './pages/ClientSetupWizard';
import { ParentClientRollup } from './pages/ParentClientRollup';
import { ParentClientsList } from './pages/ParentClientsList';
import { PropertySetupWizard } from './pages/PropertySetupWizard';
import { ParcelSetupWizard } from './pages/ParcelSetupWizard';
import { GenericListByConfig } from './pages/GenericList';
import { ContactsListPage } from './pages/ContactsListPage';
import { ContactDetail } from './pages/ContactDetail';
import { UserDetail } from './pages/UserDetail';
import { GenericDetailByConfig } from './pages/GenericDetail';
import { GroupDetail } from './pages/GroupDetail';
import { GovernmentEntityDetailByConfig } from './pages/GovernmentEntityDetail';
import { JurisdictionCreateModal } from './organisms';
import { ExemptionProposals } from './pages/ExemptionProposals';
import { ExemptionProposalDetail } from './pages/ExemptionProposalDetail';
import { TaxBillDetail } from './pages/TaxBillDetail';
import { TaxBillsList } from './pages/TaxBillsList';
import { TransmittalDetail } from './pages/TransmittalDetail';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { QuickBooksSync } from './pages/QuickBooksSync';
import { YearRoll } from './pages/YearRoll';
import { OrgSettings } from './pages/OrgSettings';
import { UserManagement } from './pages/UserManagement';
import { ReportsIndex } from './pages/ReportsIndex';
import { HelpCenter } from './pages/HelpCenter';
import { ReportViewer } from './pages/ReportViewer';
import { DocumentsList } from './pages/DocumentsList';
import { DocumentDetail } from './pages/DocumentDetail';
import { DocumentReviewQueue } from './pages/DocumentReviewQueue';
import { ParcelsList } from './pages/ParcelsList';
import { ParcelDetail } from './pages/ParcelDetail';
import { ParcelValueDetail } from './pages/ParcelValueDetail';
import { TaxRecordDetail } from './pages/TaxRecordDetail';
import { ActivityLog } from './pages/ActivityLog';
import { PropertiesList } from './pages/PropertiesList';
import { PropertyDetail } from './pages/PropertyDetail';
import { AuditLog } from './pages/AuditLog';
import { CustomFieldsAdmin } from './pages/CustomFieldsAdmin';
import { InformationRequests } from './pages/InformationRequests';
import { InformationRequestDetail } from './pages/InformationRequestDetail';
import { StateRules } from './pages/StateRules';
import { FormsLibrary } from './pages/FormsLibrary';
import { ImportWizard } from './pages/ImportWizard';
// V2 GTM surfaces (grafted from merge/be-v2-combined web tree)
import { AppealsQueues } from './pages/AppealsQueues';
import { AppealDetail } from './pages/AppealDetail';
import { ConsultantDeals } from './pages/ConsultantDeals';
import { CommissionLedger } from './pages/CommissionLedger';
import { CommissionsReport } from './pages/CommissionsReport';
import { ArReport } from './pages/ArReport';
import { ReportBuilder } from './pages/ReportBuilder';
import { Notifications } from './pages/Notifications';
import { MyEmailSettings } from './pages/MyEmailSettings';
import { Tasks } from './pages/Tasks';
// BPP (business personal property) surfaces
import { ReturnsList } from './pages/ReturnsList';
import { FiledReturnDetail } from './pages/FiledReturnDetail';
import { CalendarPage } from './pages/CalendarPage';
import { ToastProvider, RequireSuperAdmin, RequireEntity, UnlessFlag } from './organisms';
import { FEATURE } from './lib/useFlag';
import { api } from './lib/api';
import { applyOrgBranding, extractOrgBranding } from './lib/branding';

// Design system foundation (Phase 11 Block 6 PR 1)
import './styles/tokens.css';
import './styles/reset.css';
import './styles/global.css';

// Org branding boot — fetch current user (which carries org brand colors per
// Phase 11 Block 6 PR 3b backend wire) + apply CSS-variable overrides on
// the document root before the app first paints. If the fetch fails (e.g.
// not logged in yet), defaults from tokens.css remain in effect.
// Per Populus/doctrine/design-system.md § Color — System vs Brand tokens.
(async () => {
  try {
    const currentUser = await api.get('/api/current-user');
    const branding = extractOrgBranding(currentUser);
    applyOrgBranding(branding);
  } catch {
    // Not authenticated, or org branding not yet wired in API. Defaults apply.
    // Component rendering proceeds with Holdings palette.
  }
})();

// Frontend error tracking — no-op unless VITE_SENTRY_DSN is set at build. First, so it
// captures errors in the branding boot + analytics init too.
initSentry();

// PostHog product analytics — no-op unless VITE_POSTHOG_KEY is set at build.
// Additive + fail-open; identity/group happen on the authenticated side (App.tsx).
initAnalytics();

/**
 * RouteTracker — records a SPA pageview on every React Router location change.
 * PostHog is initialized with capture_pageview:false so we own this signal and
 * it fires on client-side navigation, not just the first load. Fail-open.
 */
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    // basename="/" — location.pathname is already the real (root-relative) URL.
    capturePageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// appealstack v2 entry.
//
// Stack: React 18 + TypeScript + Vite + TanStack Query + React Router per
// doctrine/language-policy.md (canonical frontend stack) + UI_REFRESH_MIGRATION.md.
//
// Routing:
//  - basename="/" — V2 owns the site root (FR-6 cutover); all client routes are
//    relative to '/'. Old /v2/* links 301-redirect to the stripped path server-side.
//  - The Express root SPA fallback serves this index.html for any non-API, non-asset
//    navigation, so React Router handles client-side navigation cleanly after load.
//
// API client: TanStack Query consumes /api/* directly (same-origin cookies).

// Toolbar status filter for the Invoice Items list. Mirrors ENUMS.invoice_items.status
// (src/config/enums.js); `pending` is the "staged, not yet on an invoice" state the
// dashboard's Billable-items card counts and deep-links to.
const INVOICE_ITEM_STATUS_FILTER = {
  column: 'status',
  placeholder: 'All statuses',
  // A billing lifecycle, pending → paid: kept in that order, not alphabetical.
  preserveOrder: true,
  options: [
    { value: 'pending', label: 'Pending (billable)' },
    { value: 'billable', label: 'Billable' },
    { value: 'invoiced', label: 'Invoiced' },
    { value: 'paid', label: 'Paid' },
    // "Non billable", not "Non-billable" — the spec's naming rule (assumption 24) calls
    // this label correct as written, and Y3 makes it a status operators actually filter on.
    { value: 'non_billable', label: 'Non billable' },
    { value: 'no_invoice', label: 'No invoice' },
    { value: 'void', label: 'Void' },
  ],
};

// Y3 (Yadira 2026-08-03) — "surface the reason so an operator can see why a win produced
// no bill." GenericList otherwise auto-derives the first 6 columns by displayOrder, which
// for invoice_items is `client_id · property_id · parcel_id · year · state · re_pp` — three
// raw UUIDs, two columns that are always "—", and no money, no status, and neither of the
// columns Y3 exists to show.
//
// A Non billable item carries invoice_id NULL (it is not on an invoice, correctly), so
// InvoiceDetail — the only other page that renders billing_reason — can never display one.
// This list is therefore the ONLY surface for it, and the "Non billable" option in
// INVOICE_ITEM_STATUS_FILTER above already filters to a set the columns could not explain.
const INVOICE_ITEM_COLUMNS = [
  'parcel_id', 'year', 'status', 'description', 'amount', 'billing_reason',
];

// Read-heavy app: every mutation explicitly invalidates its own query keys, so a
// generous staleTime is safe and makes navigation feel instant — a revisited page
// serves cache immediately (revalidated in the background) instead of re-paying the
// ~200ms server round-trip. gcTime keeps unmounted queries in memory long enough
// that clicking back and forth stays a cache hit.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,          // 1 min fresh (was 30s)
      gcTime: 60 * 60_000,        // keep cached data 1h so clicking back-and-forth is a cache hit
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// The query cache is deliberately NOT persisted to localStorage. Persisting it
// rehydrated a PRIOR session's data across a "View as" role-switch / re-login — the
// cache survived the hard reload under non-user-scoped keys (['current-user'],
// ['dashboard'], …), so a switched-into user could briefly see the previous user's
// identity + data (even the operator shell for a client). A hard reload already
// wipes the in-memory cache, so dropping persistence removes that hazard with no
// loss to in-session snappiness — that comes from staleTime/gcTime + hover-prefetch.

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <SentryErrorBoundary fallback={<div style={{ padding: '2rem', fontFamily: 'system-ui' }}>Something went wrong. Please reload.</div>}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename="/">
        <RouteTracker />
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Legal pages sit OUTSIDE the authenticated <App> tree, alongside
                /login, so a signed-out visitor (or a client-portal user who
                needs the privacy notice before signing in) can reach them. The
                server serves the SPA shell for any unauthenticated navigation,
                so these need no backend route. */}
            <Route path="/legal" element={<Navigate to="/legal/demo" replace />} />
            <Route path="/legal/demo" element={<LegalDemoNotice />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/__/atoms" element={<AtomsDemo />} />
            <Route path="/__/organisms" element={<OrganismsDemo />} />
            <Route path="/" element={<App />}>
              <Route index element={<Dashboard />} />
              <Route path="build" element={<BuildMode />} />
              <Route path="build/:id/review" element={<BuildReview />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="clients" element={<ClientsList />} />
              <Route path="clients/new" element={<ClientSetupWizard />} />
              <Route path="clients/:id" element={<ClientDetail />} />
              <Route path="law/matters" element={<MattersIndex />} />
              <Route path="law/matters/:id" element={<MatterDetail />} />
              <Route path="parent-clients" element={<UnlessFlag flag={FEATURE.HIDE_PARENT_CLIENTS} to="/clients"><ParentClientsList /></UnlessFlag>} />
              <Route path="parent-clients/:id" element={<UnlessFlag flag={FEATURE.HIDE_PARENT_CLIENTS} to="/clients"><ParentClientRollup /></UnlessFlag>} />
              <Route path="parcels" element={<ParcelsList />} />
              <Route path="parcels/new" element={<ParcelSetupWizard />} />
              {/* Government mass update (Q6+Q7): the multi-parcel-selection spine
                  applies exemption flags, caps, OR reductions across a selected set of
                  parcels — the general analogue of the jurisdiction-wide cap. (The
                  jurisdiction profile still applies a cap to every attached parcel.) */}
              <Route path="parcels/exemption-proposals" element={<ExemptionProposals />} />
              <Route path="parcels/exemption-proposals/:id" element={<ExemptionProposalDetail />} />
              <Route path="parcels/:id" element={<ParcelDetail />} />
              <Route path="properties" element={<PropertiesList />} />
              <Route path="properties/new" element={<PropertySetupWizard />} />
              <Route path="properties/:id" element={<PropertyDetail />} />

              {/* Block 9 PRs 1+2: schema-driven list pages for supporting entities.
                  Government reference entities use the bespoke tabbed
                  GovernmentEntityDetail (Overview / Contacts / Activity) — the
                  restored profile UI, not the flat GenericDetail. */}
              <Route path="assessors" element={<GenericListByConfig table="assessors" title="Assessors" columns={['name', 'short_name', 'state', 'city', 'year']} liveStateNotApplicable />} />
              <Route path="assessors/:id" element={<GovernmentEntityDetailByConfig table="assessors" title="Assessors" />} />
              <Route path="collectors" element={<GenericListByConfig table="collectors" title="Collectors" columns={['name', 'short_name', 'state', 'collector_type', 'payable_year']} />} />
              <Route path="collectors/:id" element={<GovernmentEntityDetailByConfig table="collectors" title="Collectors" />} />
              <Route path="jurisdictions" element={<GenericListByConfig table="jurisdictions" title="Jurisdictions" columns={['name', 'short_name', 'state', 'type', 'code']} renderCreateModal={({ open, onClose }) => <JurisdictionCreateModal open={open} onClose={onClose} invalidateKeys={[['jurisdictions-list']]} />} />} />
              <Route path="jurisdictions/:id" element={<GovernmentEntityDetailByConfig table="jurisdictions" title="Jurisdictions" />} />
              {/* 7/28 B1: the Contacts Type filter sources from org-settings contact-types
                  (ContactsListPage), not a hardcoded list. */}
              <Route path="contacts" element={<ContactsListPage />} />
              <Route path="contacts/:id" element={<ContactDetail />} />
              {/* Universal Groups (spec 0008) — internal only, gated on the `group` entity. */}
              <Route path="groups" element={<RequireEntity entity="group"><GenericListByConfig table="groups" title="Groups" columns={['name', 'description']} /></RequireEntity>} />
              <Route path="groups/:id" element={<RequireEntity entity="group"><GroupDetail /></RequireEntity>} />
              <Route path="documents" element={<DocumentsList />} />
              <Route path="documents/review-queue" element={<DocumentReviewQueue />} />
              <Route path="activity-log" element={<ActivityLog />} />
              <Route path="information-requests" element={<InformationRequests />} />
              <Route path="information-requests/:id" element={<InformationRequestDetail />} />
              <Route path="documents/:id" element={<DocumentDetail />} />
              <Route path="tax-bills" element={<TaxBillsList />} />
              <Route path="tax-bills/:id" element={<TaxBillDetail />} />
              {/* §8.2: the standalone Transmittals list is retired — a transmittal
                  is a tax-bill status ('transmitted'), searchable from the Tax Bills
                  list. Redirect the old list; individual transmittal detail still
                  resolves for any deep link. */}
              <Route path="transmittals" element={<Navigate to="/tax-bills" replace />} />
              <Route path="transmittals/:id" element={<TransmittalDetail />} />
              <Route path="invoices" element={<GenericListByConfig table="invoices" title="Invoices" />} />
              <Route path="quickbooks" element={<QuickBooksSync />} />
              <Route path="invoices/:id" element={<InvoiceDetail />} />
              {/* Fee/deal surfaces — super_admin-only by default, grantable via the
                  permissions matrix. Guards render an "Access restricted" panel for
                  a user without the grant (backend also default-denies the data). */}
              {/* The status filter is what makes the dashboard's "Billable items" card
                  provable: it links to /invoice-items?status=pending, and GenericList
                  seeds this filter from the URL so the list shows exactly the items
                  the card counted. */}
              <Route path="invoice-items" element={<RequireEntity entity="invoice_item"><GenericListByConfig table="invoice_items" title="Invoice Items" filterField={INVOICE_ITEM_STATUS_FILTER} columns={INVOICE_ITEM_COLUMNS} /></RequireEntity>} />
              <Route path="invoice-items/:id" element={<RequireEntity entity="invoice_item"><GenericDetailByConfig table="invoice_items" title="Invoice Items" /></RequireEntity>} />
              <Route path="invoice-terms" element={<RequireEntity entity="invoice_term"><GenericListByConfig table="invoice_terms" title="Invoice Terms" /></RequireEntity>} />
              <Route path="invoice-terms/:id" element={<RequireEntity entity="invoice_term"><InvoiceTermsDetail /></RequireEntity>} />
              {/* §8.1: the standalone Values LIST is retired — valuation entry lives
                  inside the parcel profile, so the list redirects to Parcels. The
                  per-id value card (B1) is reachable by drilling from a parcel's
                  Values tab and shows/edits that single tax-year value in place. */}
              <Route path="parcel-values" element={<Navigate to="/parcels" replace />} />
              <Route path="parcel-values/:id" element={<ParcelValueDetail />} />
              <Route path="tax-records" element={<GenericListByConfig table="tax_records" title="Tax Records" />} />
              {/* §4 — the jurisdiction-line record, to the Siger Tax Record layout. This
                  is where a cap lives (method · cap · starts · ends). Was the generic
                  schema-driven detail, which had no test of its own. */}
              <Route path="tax-records/:id" element={<TaxRecordDetail />} />
              <Route path="users" element={<RequireSuperAdmin><GenericListByConfig table="users" title="Users" disableCreate createCaption="Create users from User Management" /></RequireSuperAdmin>} />
              <Route path="users/:id" element={<RequireSuperAdmin><UserDetail /></RequireSuperAdmin>} />
              <Route path="audit-logs" element={<RequireSuperAdmin><AuditLog /></RequireSuperAdmin>} />
              <Route path="audit-logs/:id" element={<RequireSuperAdmin><GenericDetailByConfig table="audit_logs" title="Audit Log" disableEdit /></RequireSuperAdmin>} />

              {/* V2 GTM surfaces */}
              <Route path="appeals/queues" element={<AppealsQueues />} />
              <Route path="appeals/:id" element={<AppealDetail />} />
              <Route path="consultant-deals" element={<RequireEntity entity="consultant_deal"><ConsultantDeals /></RequireEntity>} />
              {/* Commission ledger (feature #43). Role-aware surface: the backend
                  returns all-consultants for firm-wide roles and own-only for
                  agent/analyst/consultant, so it is not entity-gated on the FE —
                  the /api/commissions gate is the enforcement. */}
              <Route path="commissions" element={<CommissionsReport />} />
              <Route path="commissions/ledger" element={<CommissionLedger />} />
              <Route path="ar" element={<RequireEntity entity="invoice"><ArReport /></RequireEntity>} />
              <Route path="report-builder" element={<ReportBuilder />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="account/email" element={<MyEmailSettings />} />
              <Route path="tasks" element={<Tasks />} />

              {/* BPP returns */}
              <Route path="filed-returns" element={<ReturnsList />} />
              <Route path="filed-returns/:id" element={<FiledReturnDetail />} />

              {/* Block 10 workflows */}
              <Route path="year-roll" element={<YearRoll />} />
              <Route path="org-settings" element={<RequireSuperAdmin><OrgSettings /></RequireSuperAdmin>} />
              <Route path="custom-fields" element={<CustomFieldsAdmin />} />
              <Route path="state-rules" element={<StateRules />} />
              <Route path="forms" element={<FormsLibrary />} />
              <Route path="imports" element={<ImportWizard />} />
              <Route path="user-management" element={<RequireSuperAdmin><UserManagement /></RequireSuperAdmin>} />
              <Route path="reports" element={<ReportsIndex />} />
              <Route path="reports/:slug" element={<ReportViewer />} />
              {/* Help is deliberately ungated. Somebody who lacks permission for a
                  surface still needs to be able to read what it is, and the corpus
                  ships in the bundle anyway — gating the route would hide the page
                  without hiding the content. */}
              <Route path="help" element={<HelpCenter />} />
              <Route path="help/:slug" element={<HelpCenter />} />
              <Route path="*" element={<Dashboard />} />
            </Route>
          </Routes>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </SentryErrorBoundary>
  </React.StrictMode>,
);
